import { Grid, ListItem, ListItemText } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import AutocompleteGroupedList from "../../../common/components/functional/AutocompleteGroupedList/AutocompleteGroupedList";
import AddButton from "../../../common/components/functional/addButton/AddButton";
import AutocompleteBase from "../../../common/components/functional/fields/autocompleteBase/AutocompleteBase";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../common/helpers/isEmpty";
import useMessage from "../../../hooks/useMessage";
import { WorkTitle } from "../../orders/orderAPI";
import { Talent } from "../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentWorkTitles,
} from "../../talents/talentsSlice";
import { fetchWorkTitles } from "../../workTitles/workTitleSlice";

type Props = {
  onClose: () => void;
};

const TalentEditWorkTitles = ({ onClose }: Props) => {
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();
  const { i18n, t } = useTranslation(["common", "talentProfile"]);
  const { talentId } = useParams();

  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );
  const localized = useAppSelector((state) => state.localized);

  const existingWorkTitleIds =
    talent?.workTitles?.values?.map((workTitle) => workTitle.id) || [];

  const [availableWorkTitles, setAvailableWorkTitles] = useState<WorkTitle[]>(
    [],
  );
  const [selectedWorkTitles, setSelectedWorkTitles] = useState<
    WorkTitle[] | null
  >();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isEmpty(availableWorkTitles)) {
      const language = i18n.language.substring(0, 2);
      dispatch(fetchWorkTitles({ language })).then((action) => {
        if (isFulfilled(action)) {
          const sortedWorkTitles = action.payload
            .sort((a, b) => a.name.localeCompare(b.name))
            .sort((a, b) => {
              if (a.fieldOfWorkId && b.fieldOfWorkId) {
                return a.fieldOfWorkId.localeCompare(b.fieldOfWorkId);
              }
              return 0;
            });
          setAvailableWorkTitles(sortedWorkTitles);
        }
      });
    }
  }, [availableWorkTitles, dispatch, i18n.language]);

  const onSubmit = (workTitles: WorkTitle[]) => {
    const currentWorkTitles = talent.workTitles || { values: [], version: 0 };

    const updatedWorkTitles = currentWorkTitles.values
      ? [...currentWorkTitles.values, ...workTitles]
      : workTitles;

    if (talentId) {
      setIsLoading(true);
      dispatch(
        updateTalentWorkTitles({
          talentId: talentId,
          version: currentWorkTitles.version,
          workTitles: updatedWorkTitles,
        }),
      )
        .then((action) => {
          if (isFulfilled(action)) {
            sendMessage(t("talentProfile:workTitlesUpdated"), "success");
            onClose();
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <DialogContentWrapper title={t("talentProfile:addWorkTitle")} margin>
      <OverlaySpinner isLoading={isLoading} />
      <Grid item container alignItems="center" justifyContent="center" xs={8}>
        <AutocompleteBase<WorkTitle>
          value={selectedWorkTitles || []}
          options={availableWorkTitles}
          getOptionLabel={(option) => option.name}
          getOptionDisabled={(option) =>
            existingWorkTitleIds.includes(option.id)
          }
          groupBy={(option) =>
            localized?.fieldOfWork[option.fieldOfWorkId as string]
          }
          renderOption={(props, option) => (
            <ListItem {...props} key={option.id}>
              <ListItemText>{option.name}</ListItemText>
            </ListItem>
          )}
          renderGroup={({ group, children }) => (
            <AutocompleteGroupedList
              key={group}
              group={group}
              children={children}
            />
          )}
          placeholder={t("common:searchWorkTitles")}
          onChange={(newValue) => setSelectedWorkTitles(newValue)}
        />
      </Grid>
      <Grid
        item
        container
        xs={4}
        alignItems="center"
        justifyContent="flex-start"
      >
        <AddButton
          variant="contained"
          sx={{ marginLeft: "16px" }}
          label={t("common:save")}
          disabled={!selectedWorkTitles?.length}
          onClick={() => {
            if (selectedWorkTitles) {
              onSubmit(selectedWorkTitles);
            }
          }}
        />
      </Grid>
    </DialogContentWrapper>
  );
};

export default TalentEditWorkTitles;
